<template>
    <div class="main-layout__container">
        <div class="content__header">
            <div class="content-header__title">Тарифы на доставку</div>

            <div class="content-header__actions">
                <UISimpleButton>
                    <template #icon>
                        <img v-svg-inline src="@/assets/icons/report.svg" />
                    </template>
                    Импорт из файла
                </UISimpleButton>
            </div>

            <div class="content-header__background content-header__background--2-lines-filters"></div>
        </div>

        <FilterPanel
            @search="onSearch"
            @reset="onReset"
        >
            <UISelect
                id="city_from_id"
                v-model="deliveryPricesFilter.city_from_id"
                :options="cityFromOptions"
                label="Город отправления"
                multiple
                searchable
            />

            <UISelect
                id="city_to_id"
                v-model="deliveryPricesFilter.city_to_id"
                :options="cityToOptions"
                label="Город доставки"
                multiple
                searchable
            />

            <UISelect
                id="brand_id"
                v-model="deliveryPricesFilter.brand_id"
                :options="brandOptions"
                label="Марка"
            />

            <UISelect
                id="model_id"
                v-model="deliveryPricesFilter.model_id"
                :options="modelOptions"
                label="Модель"
                multiple
                searchable
            />

            <UIInput
                id="price"
                v-model="deliveryPricesPriceFilter"
                label="Стоимость доставки"
                only-numbers
            />
        </FilterPanel>

        <UISearchResults
            :count="deliveryPricePaginationMeta?.pagination.total || 0"
            :is-visible-sort="false"
        />

        <UILoader v-if="isDeliveryPricesLoading" class="tableable" />

        <div v-else class="content__body">
            <DataTable
                :headers="headers"
                :data="arDeliveryPrices"
                :sort-key="sortKey"
                :sort-order="sortOrder"
                has-actions
                @sortable="changeSort"
            >
                <template #updated_at="{ row }">
                    {{ formatFromUTC(row.updated_at) }}
                </template>

                <template #actions="{ row }">
                    <UIDropDown as-modal-on-mobile>
                        <template #elements>
                            <router-link
                                :to="{ name: 'EditDeliveryPrice', params: { delivery_price_id: row.id } }"
                                class="ui-dropdown-menu__element"
                            >
                                <img v-svg-inline src="@/assets/icons/ui/edit.svg" />
                                <span>Редактировать</span>
                            </router-link>

                            <li
                                class="ui-dropdown-menu__element danger"
                                @click="handleDeleteDeliveryPrice(row as DeliveryPrice)"
                            >
                                <img v-svg-inline src="@/assets/icons/ui/trash.svg" />
                                <span>Удалить</span>
                            </li>
                        </template>
                    </UIDropDown>
                </template>
            </DataTable>
        </div>

        <div class="content__footer">
            <TablePagination
                :total-pages="deliveryPricePaginationMeta?.pagination.last_page ?? 1"
                :current-page="deliveryPricePaginationMeta?.pagination.current_page ?? 1"
                :items-per-page="deliveryPricePaginationMeta?.pagination.per_page ?? 10"
                @page-change="changePage"
            />
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { ref, onMounted, computed, h, inject } from 'vue';

    import { ModalPlugin } from '@/plugins/modalPlugin.ts';

    import { SelectOption } from '@/types/form.type.ts';
    import { City } from '@/types/city.type.ts';
    import { DeliveryPrice, DeliveryPricesRequestFilter } from '@/types/delivery-price.type.ts';
    import { Model } from '@/types/model.type.ts';

    import useCityRepository from '@/repositories/city/useCityRepository.ts';
    import useDeliveryPriceRepository from '@/repositories/delivery-price/useDeliveryPriceRepository.ts';
    import useModelRepository from '@/repositories/model/useModelRepository.ts';
    import useNotifications from '@/utils/useNotifications.ts';
    import { useDeliveryPricesFilterStore } from '@/stores/delivery-prices/delivery-prices-filter.ts';

    import { formatFromUTC } from '@/utils/DateTimeFormatter.ts';
    import { getBrandSelectOptions } from '@/utils/SelectOptionFillers.ts';

    import DataTable from '@/components/shared/DataTable/DataTable.vue';
    import FilterPanel from '@/components/shared/FilterPanel/FilterPanel.vue';
    import TablePagination from '@/components/shared/TablePagination/TablePagination.vue';

    import UIDropDown from '@/components/ui/UIDropDown/UIDropDown.vue';
    import UIInput from '@/components/ui/UIInput/UIInput.vue';
    import UILoader from '@/components/ui/UILoader/UILoader.vue';
    import UISearchResults from '@/components/ui/UISearchResults/UISearchResults.vue';
    import UISelect from '@/components/ui/UISelect/UISelect.vue';
    import UISimpleButton from '@/components/ui/UISimpleButton/UISimpleButton.vue';

    import ConfirmActionModal from '@/components/modals/ConfirmActionModal/ConfirmActionModal.vue';

    import DeliveryPriceConfirmDelete from '@/components/delivery-price/DeliveryPriceConfirmDelete/DeliveryPriceConfirmDelete.vue';

    const $modal = inject('$modal') as typeof ModalPlugin;

    const {
        arDeliveryPrices,
        deliveryPricePaginationMeta,
        isDeliveryPricesLoading,
        getDeliveryPrices,
        deactivateDeliveryPrice,
    } = useDeliveryPriceRepository();
    const { getAllCities } = useCityRepository();
    const { getAllActiveModels } = useModelRepository();
    const deliveryPricesFilterStore = useDeliveryPricesFilterStore();
    const { showSuccess } = useNotifications();

    const deliveryPricesFilter = ref<DeliveryPricesRequestFilter>({ ...deliveryPricesFilterStore.$state });
    const cityFromOptions = ref<SelectOption[]>([]);
    const cityToOptions = ref<SelectOption[]>([]);
    const brandOptions = ref<SelectOption[]>(getBrandSelectOptions());
    const headers = ref([
        { label: 'Город отправления', key: 'city_from_name', sortable: true },
        { label: 'Марка', key: 'brand_name', sortable: false },
        { label: 'Модель', key: 'model_name', sortable: true },
        { label: 'Город доставки', key: 'city_to_name', sortable: true },
        { label: 'Стоимость', key: 'price', sortable: true },
    ]);

    const arModels = ref<Model[]>([]);

    const deliveryPricesPriceFilter = computed<string>({
        get: () => deliveryPricesFilter.value.price === undefined ? '' : `${deliveryPricesFilter.value.price}`,
        set: (value) => {
            deliveryPricesFilter.value.price = value.match(/^\d+$/) ? parseInt(value) : undefined;
        },
    });

    const sortKey = computed<string | undefined>(() => {
        if (deliveryPricesFilter.value.sort) {
            return Object.keys(deliveryPricesFilter.value.sort[0] ?? {})[0];
        }

        return undefined;
    });

    const sortOrder = computed<string | undefined>(() => {
        if (deliveryPricesFilter.value.sort) {
            return Object.values(deliveryPricesFilter.value.sort[0] ?? {})[0];
        }

        return undefined;
    });

    const modelOptions = computed<SelectOption[]>(() => {
        return arModels.value
            .filter((model: Model) => {
                if (deliveryPricesFilter.value.brand_id === undefined) {
                    return true;
                }

                return deliveryPricesFilter.value.brand_id === model.brand_id;
            })
            .map((model: Model) => ({
                label: model.name,
                value: model.id,
            }));
    });

    function onSearch(): void {
        deliveryPricesFilter.value.page = 1;
        fetchDeliveryPrices();
    }

    function onReset(): void {
        deliveryPricesFilter.value = {
            page: 1,
            brand_id: undefined,
            model_id: undefined,
            city_from_id: undefined,
            city_to_id: undefined,
            price: undefined,
            sort: [{ city_from_name: 'asc' }],
        };

        fetchDeliveryPrices();
    }

    function changePage(page: number): void {
        deliveryPricesFilter.value.page = page;
        fetchDeliveryPrices();
    }

    function changeSort(key: string, dir: 'asc' | 'desc' | undefined): void {
        deliveryPricesFilter.value.sort = dir === undefined ? [{ city_from_name: 'asc' }] : [{ [key]: dir }];
        fetchDeliveryPrices();
    }

    function handleDeleteDeliveryPrice(deliveryPrice: DeliveryPrice): void {
        $modal.open({
            component: ConfirmActionModal,
            modalType: 'narrow',
            slots: {
                default: () =>
                    h(DeliveryPriceConfirmDelete, { deliveryPrice }),
            },
            props: {
                onConfirm: async () => {
                    await deactivateDeliveryPrice(deliveryPrice.id);

                    showSuccess({
                        title: 'Тариф на доставку удален',
                    });

                    fetchDeliveryPrices();
                },
            },
        });
    }

    function mapDeliveryPricesFilterToRequestFilter(): DeliveryPricesRequestFilter {
        const requestFilter: DeliveryPricesRequestFilter = {
            page: deliveryPricesFilter.value.page,
            sort: deliveryPricesFilter.value.sort,
        };

        if (deliveryPricesFilter.value.brand_id !== undefined) {
            requestFilter.brand_id = deliveryPricesFilter.value.brand_id;
        }

        if (deliveryPricesFilter.value.model_id !== undefined) {
            requestFilter.model_id = deliveryPricesFilter.value.model_id;
        }

        if (deliveryPricesFilter.value.city_from_id !== undefined) {
            requestFilter.city_from_id = deliveryPricesFilter.value.city_from_id;
        }

        if (deliveryPricesFilter.value.city_to_id !== undefined) {
            requestFilter.city_to_id = deliveryPricesFilter.value.city_to_id;
        }

        if (deliveryPricesFilter.value.price !== undefined) {
            requestFilter.price = deliveryPricesFilter.value.price;
        }

        return requestFilter;
    }

    function fetchDeliveryPrices(): void {
        deliveryPricesFilterStore.$patch({ ...deliveryPricesFilter.value });
        getDeliveryPrices(mapDeliveryPricesFilterToRequestFilter());
    }

    function fetchFilterOptions(): void {
        getAllCities().then((fetchedCities: City[]) => {
            cityFromOptions.value = fetchedCities.map((city: City) => {
                return {
                    label: city.name,
                    value: city.id,
                };
            });

            cityToOptions.value = fetchedCities.map((city: City) => {
                return {
                    label: city.name,
                    value: city.id,
                };
            });
        });

        getAllActiveModels().then((fetchedModels: Model[]) => {
            arModels.value = fetchedModels;
        });
    }

    onMounted(() => {
        fetchDeliveryPrices();
        fetchFilterOptions();
    });
</script>
